import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Modal,
  Typography,
  Tooltip,
  Popconfirm,
  Skeleton,
  Tag,
  Badge,
  Space,
} from "antd";
import { useMemo, useEffect, useState, useContext } from "react";
import DeviceForm from "./DeviceForm";
import useFetch from "../../../hooks/useFetch";
import { IndexContext } from "../../../context/IndexContext";
import DataTable from "../../common/DataTable";
import { useTableSearch } from "../../../functions/tableSearch";
import { render } from "@antv/g2";

const { Title } = Typography;

export default function DeviceView(props) {
  const { siteID } = props;

  const { messageApi } = useContext(IndexContext);
  const [fetchWrapper] = useFetch();

  const [loading, setLoading] = useState(false);

  const [isAddNewPowerMeterModalOpen, setIsAddNewPowerMeterModalOpen] =
    useState(false);
  const [isEditNewPowerMeterModalOpen, setIsEditNewPowerMeterModalOpen] =
    useState(false);

  const [powerMeterDetails, setPowerMeterDetails] = useState([]);
  const [selectedPowerMeterDetails, setSelectedPowerMeterDetails] =
    useState(null);

  const { getColumnSearchProps } = useTableSearch();

  const columns = [
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      ...getColumnSearchProps("Name"),
    },
    {
      title: "Device Type",
      dataIndex: "TypeName",
      key: "TypeName",
    },
    {
      title: "Key",
      dataIndex: "Key",
      key: "Key",
    },
    {
      title: "Area",
      dataIndex: "Area",
      key: "Area",
      render: (text, record) => {
        return (
          <Space>
            <>{text}</>
            {record.IsAreaSource === true ? (
              <Tag color="var(--ant-purple-6)">Area Source</Tag>
            ) : (
              <></>
            )}
          </Space>
        );
      },
    },
    {
      title: "SEU",
      dataIndex: "Seu",
      key: "Seu",
      render: (text, record) => {
        return (
          <Space>
            <>{text}</>
            {record.IsSeuSource === true ? (
              <Tag color="var(--ant-cyan-6)">SEU Source</Tag>
            ) : (
              <></>
            )}
          </Space>
        );
      },
    },
    {
      title: "Brand",
      dataIndex: "Brand",
      key: "Brand",
    },
    {
      title: "Model",
      dataIndex: "Model",
      key: "Model",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      width: 100,
      render: (_, record) => (
        <Flex>
          <div className="mr-2">
            <Tooltip title="Edit">
              <EditOutlined
                style={{ color: "blue" }}
                onClick={() => {
                  setIsEditNewPowerMeterModalOpen(true);
                  setSelectedPowerMeterDetails(record);
                }}
              />
            </Tooltip>
          </div>

          <div>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete Device"
                description={
                  "Are you sure to delete this power meter together with its parameters?"
                }
                okText="Yes"
                cancelText="No"
                onConfirm={() => {
                  deletePowerMeter(record.ID);
                }}
              >
                <DeleteOutlined style={{ color: "red" }} />
              </Popconfirm>
            </Tooltip>
          </div>
        </Flex>
      ),
    },
  ];

  /* API to get power meter details */
  function getPowerMeterDetails() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "site_management/getPowerMeterDetails",
      body: {
        site_id: siteID,
      },
      onSuccess: (fetch_output) => {
        setLoading(false);
        if (fetch_output.status === true) {
          setPowerMeterDetails(fetch_output.power_meter_details);
        }
      },
    });
  }

  /* API to delete power meter data */
  function deletePowerMeter(device_id) {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "site_management/deletePowerMeter",
      body: {
        device_id: device_id,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          messageApi.open({
            type: "success",
            content: fetch_output.message,
          });
          getPowerMeterDetails();
        } else {
          messageApi.open({
            type: "error",
            content: fetch_output.error,
          });
        }
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteID) {
      getPowerMeterDetails();
    }
  }, [siteID]);

  return (
    <>
      <div>
        <Title level={3} className="mt-0">
          Device
        </Title>
        <Flex justify="flex-start" align="center" wrap={true}>
          <Button
            icon={<PlusOutlined />}
            className="mb-5 mr-2"
            onClick={() => setIsAddNewPowerMeterModalOpen(true)}
          >
            New Device
          </Button>
        </Flex>
        {loading ? (
          <Skeleton />
        ) : (
          <DataTable
            table_column={columns}
            table_data={powerMeterDetails}
            scroll_width={700}
          />
        )}

        <Modal
          centered
          open={isAddNewPowerMeterModalOpen}
          title="Add Device"
          width={900}
          footer={false}
          style={{ width: "900px", maxWidth: "90vw" }}
          destroyOnClose={true}
          maskClosable={false}
          onCancel={() => setIsAddNewPowerMeterModalOpen(false)}
        >
          <div style={{ height: "75vh" }}>
            <DeviceForm
              siteID={siteID}
              mode="add"
              setIsPowerMeterFormModalOpen={setIsAddNewPowerMeterModalOpen}
              refreshTable={getPowerMeterDetails}
            />
          </div>
        </Modal>

        <Modal
          centered
          open={isEditNewPowerMeterModalOpen}
          title="Edit Device"
          footer={false}
          width={900}
          style={{ width: "900px", maxWidth: "90vw" }}
          destroyOnClose={true}
          maskClosable={false}
          onCancel={() => setIsEditNewPowerMeterModalOpen(false)}
        >
          <div style={{ height: "75vh" }}>
            <DeviceForm
              siteID={siteID}
              mode="edit"
              setIsPowerMeterFormModalOpen={setIsEditNewPowerMeterModalOpen}
              refreshTable={getPowerMeterDetails}
              initialValues={selectedPowerMeterDetails}
            />
          </div>
        </Modal>
      </div>
    </>
  );
}
